import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import {
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    ListItemButton,
    ListItemIcon,
    ListItemText,
} from '@mui/material';
import GetAppIcon from '@mui/icons-material/GetApp';
import { showAlert } from '../redux/slices/alerts';
import { useBrandName } from '../utils/brandUtils';
import { NavTooltip } from './NavDrawer';

const isStandalone = () =>
    (typeof window !== 'undefined' && window.matchMedia && window.matchMedia('(display-mode: standalone)').matches)
    || (typeof navigator !== 'undefined' && navigator.standalone === true);

const isIos = () =>
    typeof navigator !== 'undefined' && /iphone|ipad|ipod/i.test(navigator.userAgent)
    && !/FxiOS/i.test(navigator.userAgent);

let capturedInstallPrompt = null;

if (typeof window !== 'undefined') {
    window.addEventListener('beforeinstallprompt', (event) => {
        event.preventDefault();
        capturedInstallPrompt = event;
    });
    window.addEventListener('appinstalled', () => {
        capturedInstallPrompt = null;
    });
}

const InstallAppButton = ({ collapsed }) => {
    const dispatch = useDispatch();
    const brandName = useBrandName();
    const [deferredPrompt, setDeferredPrompt] = useState(capturedInstallPrompt);
    const [installed, setInstalled] = useState(isStandalone());
    const [instructionsOpen, setInstructionsOpen] = useState(false);
    const ios = isIos();

    useEffect(() => {
        const onBeforeInstall = (e) => {
            e.preventDefault();
            setDeferredPrompt(e);
        };
        const onAppInstalled = () => {
            setInstalled(true);
            setDeferredPrompt(null);
        };

        setDeferredPrompt((current) => current || capturedInstallPrompt);

        window.addEventListener('beforeinstallprompt', onBeforeInstall);
        window.addEventListener('appinstalled', onAppInstalled);
        return () => {
            window.removeEventListener('beforeinstallprompt', onBeforeInstall);
            window.removeEventListener('appinstalled', onAppInstalled);
        };
    }, []);

    if (installed) return null;
    if (!ios && !deferredPrompt) return null;

    const handleInstall = async () => {
        if (ios) {
            setInstructionsOpen(true);
            return;
        }
        try {
            deferredPrompt.prompt();
            const choice = await deferredPrompt.userChoice;
            if (choice?.outcome === 'accepted') {
                dispatch(showAlert({ severity: 'success', message: `${brandName} is installing — look for it on your home screen.` }));
            }
            capturedInstallPrompt = null;
            setDeferredPrompt(null);
        } catch (err) {
            dispatch(showAlert({ severity: 'error', message: err.message || 'Install failed.' }));
        }
    };

    return (
        <>
            <NavTooltip collapsed={collapsed} label={`Install ${brandName} — adds an icon to your device and opens full screen, without the browser bars.`}>
                <ListItemButton key="InstallApp" onClick={handleInstall}>
                    <ListItemIcon>
                        <GetAppIcon color="primary" />
                    </ListItemIcon>
                    <ListItemText primary={`Install ${brandName}`} />
                </ListItemButton>
            </NavTooltip>
            <Dialog open={instructionsOpen} onClose={() => setInstructionsOpen(false)} maxWidth="xs" fullWidth>
                <DialogTitle>Add {brandName} to your Home Screen</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        On iPhone and iPad you install {brandName} from your browser's own menu. Open the Share menu in this browser — the square with an arrow pointing up — then choose
                        "Add to Home Screen". Open {brandName} from the new icon to get the full-screen app.
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button variant="contained" onClick={() => setInstructionsOpen(false)}>
                        Got it
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
};

export default InstallAppButton;
